import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../store/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { ConfirmDialog } from "../../components/common/ConfirmDialog";
import { PageHeader } from "../../components/common/PageHeader";
import { SelectField } from "../../components/common/Form";
import { NotificationCenterService } from "../../services";
import { UsersService } from "../../services";
import { dateTime } from "../../utils/format";
import { toast } from "sonner";
import {
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Clock3,
  Copy,
  Loader2,
  MessageCircle,
  Plus,
  QrCode,
  RefreshCw,
  RotateCcw,
  Trash2,
  Users,
  XCircle,
} from "lucide-react";

const TABS = [
  { id: "connection", label: "Connection" },
  { id: "rules", label: "Message Rules" },
  { id: "destinations", label: "Destinations" },
  { id: "history", label: "Message History" },
];

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const eventLabel = (value = "") => value.toLowerCase().split("_").map((word) =>
  word ? `${word[0].toUpperCase()}${word.slice(1)}` : ""
).join(" ");

const recipientSummary = (rule) => {
  const usersCount = Number(rule.userDestinationCount || 0);
  const groupsCount = Number(rule.groupDestinationCount || 0);
  if (usersCount + groupsCount === 0) return "No recipients";
  return [
    usersCount > 0 ? `${usersCount} ${usersCount === 1 ? "user" : "users"}` : null,
    groupsCount > 0 ? `${groupsCount} ${groupsCount === 1 ? "group" : "groups"}` : null,
  ].filter(Boolean).join(" · ");
};

const statusClass = (status) => {
  if (status === "CONNECTED" || status === "SENT" || status === "AVAILABLE") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (status === "FAILED" || status === "UNAVAILABLE" || status === "ERROR") return "border-rose-200 bg-rose-50 text-rose-700";
  if (status === "PENDING" || status === "CONNECTING" || status === "PROCESSING" || status === "QR_REQUIRED") return "border-amber-200 bg-amber-50 text-amber-800";
  return "border-slate-200 bg-slate-100 text-slate-600";
};

const StatusPill = ({ value, label }) => (
  <Badge variant="outline" className={statusClass(value)}>{label || value || "UNKNOWN"}</Badge>
);

const SectionTab = ({ active, children, onClick }) => (
  <button type="button" onClick={onClick} aria-current={active ? "page" : undefined}
    className={`shrink-0 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors ${active ? "border-gold-500 text-navy-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
    {children}
  </button>
);

const EmptyState = ({ children }) => <div className="px-4 py-9 text-center text-sm text-slate-500">{children}</div>;

const SAMPLE_VALUES = {
  invoiceNo: "INV-0001",
  bottleQuantity: "10",
  totalPrice: "25000.00",
  supplier: "Sample Supplier",
  addedBy: "Admin User",
  dateTime: "2026-01-01 10:30",
  billNumber: "BILL-0001",
  amount: "25000.00",
  requestedBy: "Sample User",
  approvedBy: "Admin User",
  rejectedBy: "Admin User",
  reason: "Sample reason",
  settlementType: "BAR_OWNER",
  businessDate: "2026-01-01",
  confirmedBy: "Admin User",
  productName: "Sample Product",
  availableQuantity: "5",
  lowStockLevel: "10",
  reportDate: "2026-01-01",
  message: "Sample report message",
  orderNumber: "LO-20261101-00005",
  supplierName: "DCSL",
  placedBy: "Kreshan Jayamanna",
  paymentMethod: "Cash",
  grandTotal: "48,500.00",
};

const samplePreview = (template = "") => template.replace(/\{([A-Za-z][A-Za-z0-9]*)\}/g, (token, key) => SAMPLE_VALUES[key] ?? `[${key}]`);
const isStockScheduleEvent = (eventType) => ["DAILY_BAR_STOCKS", "DAILY_RESTAURANT_STOCKS"].includes(eventType);
const isStockPdfEvent = isStockScheduleEvent;
const newResendKey = (id) => globalThis.crypto?.randomUUID?.() || `${id}-${Date.now()}-${Math.random()}`;

export const WhatsAppNotificationsPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.roleName === "ADMIN";
  const [activeTab, setActiveTab] = useState("connection");

  const [connection, setConnection] = useState(null);
  const [connectionLoading, setConnectionLoading] = useState(false);
  const [connectionError, setConnectionError] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [qrCode, setQrCode] = useState("");
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState("");
  const [qrOpen, setQrOpen] = useState(false);

  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [syncingGroups, setSyncingGroups] = useState(false);
  const [groupsError, setGroupsError] = useState("");

  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesError, setRulesError] = useState("");
  const [selectedRuleId, setSelectedRuleId] = useState("");
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleSaving, setRuleSaving] = useState(false);
  const [ruleForm, setRuleForm] = useState({ enabled: false, whatsappEnabled: false, whatsappActionsEnabled: false, messageTemplate: "", scheduleTime: "10:30" });
  const [ruleError, setRuleError] = useState("");
  const templateRef = useRef(null);

  const [destinations, setDestinations] = useState([]);
  const [destinationsLoading, setDestinationsLoading] = useState(false);
  const [destinationsError, setDestinationsError] = useState("");
  const destinationLoadId = useRef(0);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [addDestinationOpen, setAddDestinationOpen] = useState(false);
  const [destinationType, setDestinationType] = useState("USER");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [destinationSaving, setDestinationSaving] = useState(false);
  const [destinationToRemove, setDestinationToRemove] = useState(null);
  const [removingDestination, setRemovingDestination] = useState(false);

  const [outbox, setOutbox] = useState({ items: [], page: 0, size: 25, totalElements: 0, totalPages: 0 });
  const outboxRef = useRef(outbox);
  const [outboxLoading, setOutboxLoading] = useState(false);
  const [outboxError, setOutboxError] = useState("");
  const [outboxDetail, setOutboxDetail] = useState(null);
  const [retryingId, setRetryingId] = useState(null);
  const [resendTarget, setResendTarget] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const resendingLock = useRef(false);
  const resendKeys = useRef(new Map());
  const outboxRequestInProgress = useRef(null);

  const currentConnection = connection || {};
  const currentRule = useMemo(() => rules.find((rule) => String(rule.ruleId) === String(selectedRuleId)) || null, [rules, selectedRuleId]);
  const activeDestinations = useMemo(() => destinations.filter((row) => row.destinationType === "USER"), [destinations]);
  const activeGroupDestinations = useMemo(() => destinations.filter((row) => row.destinationType === "WHATSAPP_GROUP"), [destinations]);
  const liquorOrderEvent = currentRule?.eventType?.startsWith("LIQUOR_");
  const selectableUsers = useMemo(() => users.filter((user) => user.status === "ACTIVE"
    && Boolean(user.whatsappNumber?.trim())
    && (!liquorOrderEvent || ["ADMIN", "OWNER"].includes(user.roleName))
    && !destinations.some((destination) => destination.destinationType === "USER" && Number(destination.userId) === Number(user.userId))), [users, destinations, liquorOrderEvent]);
  const selectableGroups = useMemo(() => groups.filter((group) => group.available && !destinations.some((destination) => destination.destinationType === "WHATSAPP_GROUP" && Number(destination.groupId) === Number(group.id))), [groups, destinations]);
  const canAddDestination = Boolean(isStockScheduleEvent(currentRule?.eventType) || (currentRule?.enabled && currentRule?.whatsappEnabled));

  const refreshStatus = useCallback(async ({ quiet = false, notify = false } = {}) => {
    if (!quiet) setConnectionLoading(true);
    setConnectionError("");
    try {
      const value = await NotificationCenterService.getWhatsAppStatus();
      setConnection(value);
      if (notify) toast.success("WhatsApp status refreshed");
      if (value?.connected) {
        setQrOpen(false);
        setQrCode("");
      }
      return value;
    } catch (error) {
      const message = "Unable to refresh WhatsApp status";
      setConnectionError(message);
      if (notify) toast.error(message);
      return null;
    } finally {
      if (!quiet) setConnectionLoading(false);
    }
  }, []);

  const loadGroups = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setGroupsLoading(true);
    setGroupsError("");
    try {
      const value = await NotificationCenterService.getWhatsAppGroups();
      setGroups(Array.isArray(value) ? value : []);
    } catch (error) {
      const message = errorMessage(error, "Unable to load WhatsApp groups.");
      setGroupsError(message);
      if (!quiet) toast.error(message);
    } finally {
      if (!quiet) setGroupsLoading(false);
    }
  }, []);

  const loadRules = useCallback(async () => {
    setRulesLoading(true);
    setRulesError("");
    try {
      const value = await NotificationCenterService.getNotificationRules();
      const list = Array.isArray(value) ? value : [];
      setRules(list);
      setSelectedRuleId((previous) => list.some((rule) => String(rule.ruleId) === String(previous)) ? previous : String(list[0]?.ruleId || ""));
    } catch (error) {
      const message = errorMessage(error, "Unable to load notification rules.");
      setRulesError(message);
      toast.error(message);
    } finally {
      setRulesLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const value = await UsersService.list();
      setUsers(Array.isArray(value) ? value : []);
    } catch (error) {
      toast.error(errorMessage(error, "Unable to load users."));
    } finally {
      setUsersLoading(false);
      setUsersLoaded(true);
    }
  }, []);

  const loadDestinations = useCallback(async (ruleId = selectedRuleId) => {
    const requestId = ++destinationLoadId.current;
    if (!ruleId) {
      setDestinations([]);
      setDestinationsLoading(false);
      return;
    }
    setDestinationsLoading(true);
    setDestinationsError("");
    try {
      const value = await NotificationCenterService.getRuleDestinations(ruleId);
      if (requestId === destinationLoadId.current) setDestinations(Array.isArray(value) ? value : []);
    } catch (error) {
      const message = errorMessage(error, "Unable to load rule destinations.");
      if (requestId === destinationLoadId.current) {
        setDestinationsError(message);
        toast.error(message);
      }
    } finally {
      if (requestId === destinationLoadId.current) setDestinationsLoading(false);
    }
  }, [selectedRuleId]);

  const loadOutbox = useCallback(async (page, size, { quiet = false } = {}) => {
    if (outboxRequestInProgress.current) {
      if (quiet) return;
      await outboxRequestInProgress.current;
    }
    if (outboxRequestInProgress.current) return;
    if (!quiet) setOutboxLoading(true);
    setOutboxError("");
    const request = (async () => {
      try {
        const result = await NotificationCenterService.getNotificationOutbox({ page, size });
        setOutbox({
          items: result?.items || [],
          page: Number(result?.page ?? page),
          size: Number(result?.size ?? size),
          totalElements: Number(result?.totalElements || 0),
          totalPages: Number(result?.totalPages || 0),
        });
      } catch (error) {
        const message = errorMessage(error, "Unable to load notification history.");
        if (!quiet) {
          setOutboxError(message);
          toast.error(message);
        }
      } finally {
        if (!quiet) setOutboxLoading(false);
      }
    })();
    outboxRequestInProgress.current = request;
    try {
      await request;
    } finally {
      if (outboxRequestInProgress.current === request) outboxRequestInProgress.current = null;
    }
  }, []);

  useEffect(() => {
    const loadConnection = async () => {
      await refreshStatus();
    };
    loadConnection();
    loadGroups();
    loadRules();
  }, [refreshStatus, loadGroups, loadRules]);

  useEffect(() => {
    if (activeTab === "destinations" && selectedRuleId) loadDestinations(selectedRuleId);
  }, [activeTab, selectedRuleId, loadDestinations]);

  useEffect(() => {
    if (activeTab === "destinations" && !usersLoaded && !usersLoading) loadUsers();
  }, [activeTab, usersLoaded, usersLoading, loadUsers]);

  useEffect(() => {
    outboxRef.current = outbox;
  }, [outbox]);

  useEffect(() => {
    if (activeTab === "history") loadOutbox(outboxRef.current.page, outboxRef.current.size);
  }, [activeTab, loadOutbox]);

  const hasInFlightOutboxRows = outbox.items.some((row) => row.status === "PENDING" || row.status === "PROCESSING");
  useEffect(() => {
    if (activeTab !== "history" || !hasInFlightOutboxRows) return undefined;
    const interval = window.setInterval(() => {
      const latest = outboxRef.current;
      loadOutbox(latest.page, latest.size, { quiet: true });
    }, 5000);
    return () => window.clearInterval(interval);
  }, [activeTab, hasInFlightOutboxRows, loadOutbox]);

  useEffect(() => {
    if (!qrOpen || !currentConnection.qrRequired || currentConnection.connected) return undefined;
    const interval = window.setInterval(async () => {
      const value = await refreshStatus({ quiet: true });
      if (value?.connected || !value?.qrRequired) {
        setConnection(value);
        setQrOpen(false);
        setQrCode("");
      }
    }, 6000);
    return () => window.clearInterval(interval);
  }, [qrOpen, currentConnection.qrRequired, currentConnection.connected, refreshStatus, qrCode]);

  const fetchQr = async () => {
    setQrLoading(true);
    setQrError("");
    try {
      const response = await NotificationCenterService.getWhatsAppQr();
      if (response?.qrRequired === false) {
        await refreshStatus({ quiet: true });
        setQrOpen(false);
        setQrCode("");
        return;
      }
      const value = typeof response?.data === "string" ? response.data : "";
      if (!value) throw new Error("The backend did not return a QR code.");
      setQrCode(value);
      setQrOpen(true);
    } catch (error) {
      const message = errorMessage(error, "QR code is currently unavailable.");
      setQrError(message);
      toast.error(message);
    } finally {
      setQrLoading(false);
    }
  };

  const startConnection = async () => {
    setConnecting(true);
    setConnectionError("");
    try {
      await NotificationCenterService.connectWhatsApp();
      toast.success("WhatsApp connection started.");
      const value = await refreshStatus();
      if (value?.qrRequired === true && value?.connected === false) await fetchQr();
    } catch (error) {
      const message = errorMessage(error, "Unable to start WhatsApp connection.");
      setConnectionError(message);
      toast.error(message);
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    setDisconnecting(true);
    try {
      const value = await NotificationCenterService.disconnectWhatsApp();
      setConnection(value);
      setQrCode("");
      setQrOpen(false);
      toast.success("WhatsApp disconnected. Rules and history were preserved.");
      await refreshStatus();
    } catch (error) {
      toast.error(errorMessage(error, "Unable to disconnect WhatsApp."));
    } finally {
      setDisconnecting(false);
      setDisconnectOpen(false);
    }
  };

  const syncGroups = async () => {
    setSyncingGroups(true);
    setGroupsError("");
    try {
      await NotificationCenterService.syncWhatsAppGroups();
      await loadGroups({ quiet: true });
      toast.success("WhatsApp groups synchronized");
    } catch (error) {
      const message = errorMessage(error, "Unable to synchronize WhatsApp groups.");
      setGroupsError(message);
      toast.error(message);
    } finally {
      setSyncingGroups(false);
    }
  };

  const openRuleEditor = async (rule) => {
    setRuleError("");
    setRuleDialogOpen(true);
    setEditingRule(rule);
    setRuleForm({ enabled: Boolean(rule.enabled), whatsappEnabled: Boolean(rule.whatsappEnabled), whatsappActionsEnabled: Boolean(rule.whatsappActionsEnabled), messageTemplate: rule.messageTemplate || "", scheduleTime: rule.scheduleTime || "10:30" });
    try {
      const fresh = await NotificationCenterService.getNotificationRule(rule.eventType);
      setEditingRule(fresh);
      setRuleForm({ enabled: Boolean(fresh.enabled), whatsappEnabled: Boolean(fresh.whatsappEnabled), whatsappActionsEnabled: Boolean(fresh.whatsappActionsEnabled), messageTemplate: fresh.messageTemplate || "", scheduleTime: fresh.scheduleTime || "10:30" });
    } catch (error) {
      setRuleError(errorMessage(error, "Unable to load this notification rule."));
    }
  };

  const saveRule = async () => {
    if (!editingRule) return;
    setRuleSaving(true);
    setRuleError("");
    try {
      const updated = await NotificationCenterService.updateNotificationRule(editingRule.eventType, {
        enabled: ruleForm.enabled,
        whatsappEnabled: ruleForm.whatsappEnabled,
        whatsappActionsEnabled: ruleForm.whatsappActionsEnabled,
        messageTemplate: ruleForm.messageTemplate,
        scheduleTime: isStockScheduleEvent(editingRule.eventType) ? ruleForm.scheduleTime : null,
      });
      setRules((current) => current.map((rule) => rule.eventType === updated.eventType ? updated : rule));
      setEditingRule(updated);
      toast.success("Notification rule saved.");
      setRuleDialogOpen(false);
    } catch (error) {
      const message = errorMessage(error, "Unable to save notification rule.");
      setRuleError(message);
      toast.error(message);
    } finally {
      setRuleSaving(false);
    }
  };

  const insertPlaceholder = (placeholder) => {
    const area = templateRef.current;
    if (!area) {
      setRuleForm((current) => ({ ...current, messageTemplate: `${current.messageTemplate}${placeholder}` }));
      return;
    }
    const start = area.selectionStart;
    const end = area.selectionEnd;
    const next = `${ruleForm.messageTemplate.slice(0, start)}${placeholder}${ruleForm.messageTemplate.slice(end)}`;
    setRuleForm((current) => ({ ...current, messageTemplate: next }));
    requestAnimationFrame(() => {
      area.focus();
      area.setSelectionRange(start + placeholder.length, start + placeholder.length);
    });
  };

  const addDestination = async (destinationType, id) => {
    if (!currentRule?.ruleId) return;
    setDestinationSaving(true);
    try {
      const payload = destinationType === "USER"
        ? { destinationType, userId: Number(id), groupId: null }
        : { destinationType, userId: null, groupId: Number(id) };
      await NotificationCenterService.addRuleDestination(currentRule.ruleId, payload);
      toast.success("Destination added to this rule.");
      setAddDestinationOpen(false);
      setSelectedUserId("");
      setSelectedGroupId("");
      await loadDestinations(currentRule.ruleId);
      await loadRules();
    } catch (error) {
      toast.error(errorMessage(error, "Unable to add destination."));
    } finally {
      setDestinationSaving(false);
    }
  };

  const removeDestination = async () => {
    if (!destinationToRemove) return;
    setRemovingDestination(true);
    try {
      await NotificationCenterService.removeRuleDestination(destinationToRemove.destinationId);
      toast.success("Destination removed. Existing message history was preserved.");
      setDestinationToRemove(null);
      await loadDestinations(currentRule?.ruleId);
      await loadRules();
    } catch (error) {
      toast.error(errorMessage(error, "Unable to remove destination."));
    } finally {
      setRemovingDestination(false);
    }
  };

  const retryOutbox = async (row) => {
    if (!row || retryingId !== null) return;
    setRetryingId(row.outboxId);
    try {
      await NotificationCenterService.retryNotificationOutbox(row.outboxId);
      toast.success("Retry queued. The backend dispatcher will process it.");
      await loadOutbox(outbox.page, outbox.size);
    } catch (error) {
      toast.error(errorMessage(error, "Unable to queue notification retry."));
    } finally {
      setRetryingId(null);
    }
  };

const resendOutbox = async () => {
    const row = resendTarget;
    if (!row || !isAdmin || resendingLock.current) return;
    resendingLock.current = true;
    setResendingId(row.outboxId);
    try {
      const requestKey = resendKeys.current.get(row.outboxId) || newResendKey(row.outboxId);
      resendKeys.current.set(row.outboxId, requestKey);
      await NotificationCenterService.resendNotificationOutbox(row.outboxId, requestKey);
      toast.success("Resend queued for the original destination.");
      resendKeys.current.delete(row.outboxId);
      setResendTarget(null);
      await loadOutbox(outbox.page, outbox.size);
    } catch (error) {
      toast.error(errorMessage(error, "Unable to queue this resend."));
    } finally {
      resendingLock.current = false;
      setResendingId(null);
    }
  };

  const qrSource = qrCode.startsWith("data:") ? qrCode : `data:image/png;base64,${qrCode}`;
  const connectionStatus = currentConnection.connected ? "CONNECTED" : (currentConnection.state || (currentConnection.configured ? "DISCONNECTED" : "NOT_CONFIGURED"));
  const currentRulePlaceholders = Array.isArray(editingRule?.allowedPlaceholders) ? editingRule.allowedPlaceholders : [];

  return (
    <div className="space-y-4">
      <PageHeader title="WhatsApp & Notifications" subtitle="Manage the WhatsApp account, message rules, recipients and delivery history." />

      <nav className="flex gap-1 overflow-x-auto border-b border-slate-200" aria-label="WhatsApp and notification sections">
        {TABS.map((tab) => <SectionTab key={tab.id} active={activeTab === tab.id} onClick={() => setActiveTab(tab.id)}>{tab.label}</SectionTab>)}
      </nav>

      {activeTab === "connection" && <div className="space-y-4">
        <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-emerald-50 text-emerald-700"><MessageCircle className="h-5 w-5" /></div>
              <div>
                <h2 className="font-display text-base font-semibold text-slate-900">{currentConnection.connected ? "WhatsApp Account Connected" : "WhatsApp Account"}</h2>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <StatusPill value={connectionStatus} label={connectionStatus === "NOT_CONFIGURED" ? "Not configured" : connectionStatus.replaceAll("_", " ")} />
                  {currentConnection.configured && <StatusPill value={currentConnection.connected ? "CONNECTED" : "DISCONNECTED"} label={currentConnection.connected ? "Connected" : "Disconnected"} />}
                </div>
                {(currentConnection.account || currentConnection.sessionName) && <p className="mt-2 text-sm text-slate-600">{currentConnection.sessionName && <span>Session: {currentConnection.sessionName}</span>}{currentConnection.account && currentConnection.sessionName && <span> · </span>}{currentConnection.account && <span>Account: {currentConnection.account}</span>}</p>}
                {currentConnection.message && <p className="mt-2 text-sm text-slate-600">{currentConnection.message}</p>}
                {connectionError && <p className="mt-2 flex items-start gap-2 text-sm text-rose-700"><CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />{connectionError}</p>}
                {!currentConnection.configured && !connectionLoading && <p className="mt-2 text-sm text-amber-800">WhatsApp provider configuration is unavailable. Ask an administrator to configure the backend connection.</p>}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => refreshStatus({ notify: true })} disabled={connectionLoading || connecting || disconnecting}>{connectionLoading ? <Loader2 className="animate-spin" /> : <RefreshCw />}{connectionLoading ? "Refreshing..." : "Refresh Status"}</Button>
              {!currentConnection.connected && <Button onClick={startConnection} disabled={!currentConnection.configured || connecting || connectionLoading} className="bg-navy-800 text-white hover:bg-navy-900">{connecting ? <Loader2 className="animate-spin" /> : <QrCode />}Connect WhatsApp</Button>}
              {currentConnection.connected && <Button variant="outline" className="border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => setDisconnectOpen(true)} disabled={disconnecting}><XCircle />Disconnect WhatsApp</Button>}
            </div>
          </div>

          {qrOpen && currentConnection.qrRequired && !currentConnection.connected && <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50/60 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div><h3 className="font-semibold text-slate-900">WhatsApp Login</h3><p className="mt-1 text-sm text-slate-600">Scan this QR using WhatsApp on the phone you want to connect.</p></div>
              <div className="flex gap-2">
                {!qrOpen && <Button size="sm" variant="outline" onClick={fetchQr} disabled={qrLoading}>{qrLoading ? <Loader2 className="animate-spin" /> : <QrCode />}Show QR</Button>}
                {qrOpen && <Button size="sm" variant="outline" onClick={fetchQr} disabled={qrLoading}>{qrLoading ? <Loader2 className="animate-spin" /> : <RefreshCw />}Refresh QR</Button>}
                {qrOpen && <Button size="sm" variant="ghost" onClick={() => { setQrOpen(false); setQrCode(""); }}>Hide</Button>}
              </div>
            </div>
            {qrError && <p className="mt-3 text-sm text-rose-700">{qrError}</p>}
            {qrOpen && <div className="mt-4 flex flex-col items-center gap-3 rounded-lg bg-white p-4 sm:w-fit">
              {qrCode ? <img src={qrSource} alt="WhatsApp authentication QR code" className="h-56 w-56 object-contain" /> : <div className="grid h-56 w-56 place-items-center text-slate-400">{qrLoading ? <Loader2 className="h-8 w-8 animate-spin" /> : "QR unavailable"}</div>}
              <p className="flex items-center gap-2 text-sm text-slate-600"><Clock3 className="h-4 w-4" />Waiting for connection...</p>
            </div>}
          </div>}
        </section>

        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 sm:px-5">
            <div><h2 className="font-display text-base font-semibold text-slate-900">WhatsApp Groups</h2><p className="mt-0.5 text-xs text-slate-500">Groups are synced from the connected WhatsApp account.</p></div>
            <Button variant="outline" size="sm" onClick={syncGroups} disabled={!currentConnection.connected || syncingGroups}>{syncingGroups ? <Loader2 className="animate-spin" /> : <RefreshCw />}{syncingGroups ? "Syncing..." : "Sync Groups"}</Button>
          </div>
          {!currentConnection.connected && <p className="border-b border-slate-100 bg-slate-50 px-4 py-2.5 text-sm text-slate-600 sm:px-5">Connect WhatsApp before syncing groups.</p>}
          {groupsError && <p className="px-4 pt-3 text-sm text-rose-700 sm:px-5">{groupsError}</p>}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-4 py-3 text-left font-medium sm:px-5">Group Name</th><th className="px-4 py-3 text-left font-medium">Availability</th><th className="px-4 py-3 text-left font-medium">Last Synced</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {groupsLoading ? <tr><td colSpan={3}><EmptyState>Loading WhatsApp groups…</EmptyState></td></tr> : groups.length === 0 ? <tr><td colSpan={3}><EmptyState>No WhatsApp groups are saved. Connect WhatsApp and sync groups to get started.</EmptyState></td></tr> : groups.map((group) => <tr key={group.id}><td className="px-4 py-3 font-medium text-slate-800 sm:px-5">{group.groupName}</td><td className="px-4 py-3"><StatusPill value={group.available ? "AVAILABLE" : "UNAVAILABLE"} label={group.available ? "Available" : "Unavailable"} /></td><td className="px-4 py-3 text-slate-600">{group.lastSyncedAt ? dateTime(group.lastSyncedAt) : "—"}</td></tr>)}
              </tbody>
            </table>
          </div>
        </section>
      </div>}

      {activeTab === "rules" && <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 sm:px-5"><div><h2 className="font-display text-base font-semibold text-slate-900">Notifications</h2><p className="mt-0.5 text-xs text-slate-500">Choose who receives each WhatsApp notification and what it says.</p></div><Button variant="outline" size="sm" onClick={loadRules} disabled={rulesLoading}>{rulesLoading ? <Loader2 className="animate-spin" /> : <RefreshCw />}Refresh</Button></div>
        {rulesError && <p className="px-4 pt-3 text-sm text-rose-700 sm:px-5">{rulesError}</p>}
        {rulesLoading ? <EmptyState>Loading notifications…</EmptyState> : rules.length === 0 ? <EmptyState>No notification rules are configured.</EmptyState> : <>
          <div className="hidden md:block"><table className="min-w-full text-sm"><thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-4 py-3 text-left font-medium sm:px-5">Notification</th><th className="px-4 py-3 text-left font-medium">WhatsApp Status</th><th className="px-4 py-3 text-left font-medium">Recipients</th><th className="px-4 py-3 text-right font-medium">Action</th></tr></thead><tbody className="divide-y divide-slate-100">
            {rules.map((rule) => { const statusLabel = !rule.enabled ? "Notifications Off" : rule.whatsappEnabled ? "WhatsApp On" : "WhatsApp Off"; return <tr key={rule.ruleId}><td className="px-4 py-3 sm:px-5"><div className="font-medium text-slate-900">{eventLabel(rule.eventType)}</div><p className="mt-0.5 max-w-2xl text-xs leading-5 text-slate-500">{rule.description}</p><div className="mt-0.5 text-[11px] text-slate-400">{rule.eventType}</div></td><td className="px-4 py-3"><StatusPill value={rule.enabled && rule.whatsappEnabled ? "CONNECTED" : "DISCONNECTED"} label={statusLabel} /></td><td className="px-4 py-3 text-sm text-slate-700">{recipientSummary(rule)}</td><td className="px-4 py-3 text-right"><Button size="sm" variant="outline" onClick={() => openRuleEditor(rule)}>Configure</Button></td></tr>; })}
          </tbody></table></div>
          <div className="divide-y divide-slate-100 md:hidden">{rules.map((rule) => { const statusLabel = !rule.enabled ? "Notifications Off" : rule.whatsappEnabled ? "WhatsApp On" : "WhatsApp Off"; return <article key={rule.ruleId} className="space-y-2 p-4"><div className="flex items-start justify-between gap-3"><div className="min-w-0"><h3 className="font-medium text-slate-900">{eventLabel(rule.eventType)}</h3><p className="mt-1 text-sm leading-5 text-slate-600">{rule.description}</p></div><StatusPill value={rule.enabled && rule.whatsappEnabled ? "CONNECTED" : "DISCONNECTED"} label={statusLabel} /></div><p className="text-sm text-slate-600">{recipientSummary(rule)}</p><div className="flex items-center justify-between gap-2"><span className="text-[11px] text-slate-400">{rule.eventType}</span><Button size="sm" variant="outline" onClick={() => openRuleEditor(rule)}>Configure</Button></div></article>; })}</div>
        </>}
      </section>}

      {activeTab === "destinations" && <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-end justify-between gap-3 border-b border-slate-100 px-4 py-3 sm:px-5">
          <div className="min-w-56 flex-1"><SelectField label="Notification" value={selectedRuleId} onChange={(value) => { destinationLoadId.current += 1; setDestinations([]); setDestinationsError(""); setDestinationsLoading(Boolean(value)); setSelectedRuleId(value); }} options={rules.map((rule) => ({ value: rule.ruleId, label: eventLabel(rule.eventType) }))} placeholder="Select a notification" disabled={!rules.length} /></div>
          <Button size="sm" variant="outline" onClick={() => { setSelectedUserId(""); setSelectedGroupId(""); setDestinationType("USER"); setAddDestinationOpen(true); }} disabled={!canAddDestination}><Plus />Add Recipient</Button>
        </div>
        {destinationsError && <p className="px-4 pt-3 text-sm text-rose-700 sm:px-5">{destinationsError}</p>}
        {!currentRule ? <EmptyState>Select a notification to manage its recipients.</EmptyState> : destinationsLoading ? <EmptyState>Loading recipients…</EmptyState> : <><div className="border-b border-slate-100 bg-slate-50 px-4 py-3 sm:px-5"><h3 className="font-medium text-slate-900">{eventLabel(currentRule.eventType)}</h3><p className="mt-1 text-sm text-slate-600">{currentRule.description}</p><p className="mt-2 text-xs font-medium text-slate-500">{!currentRule.enabled ? "Notifications are off" : currentRule.whatsappEnabled ? "WhatsApp is on" : "WhatsApp is off"}</p>{!canAddDestination && <p className="mt-1 text-xs text-amber-800">Existing recipients are preserved. Turn this notification and WhatsApp on before adding recipients.</p>}</div><div className="grid gap-0 divide-y divide-slate-100 md:grid-cols-2 md:divide-x md:divide-y-0">
          <div><div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-3 sm:px-5"><Users className="h-4 w-4 text-slate-500" /><h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">Users</h3><span className="text-xs text-slate-400">{activeDestinations.length}</span></div>{activeDestinations.length === 0 ? <EmptyState>No user recipients configured.</EmptyState> : <ul className="divide-y divide-slate-100">{activeDestinations.map((destination) => { const user = users.find((row) => Number(row.userId) === Number(destination.userId)); return <li key={destination.destinationId} className="flex items-center justify-between gap-3 px-4 py-3 sm:px-5"><div className="min-w-0"><div className="truncate text-sm font-medium text-slate-800">{destination.userName || user?.fullName || "User"}</div><div className="truncate text-xs text-slate-500">{user?.roleName ? user.roleName.toLowerCase().replace(/(^|_)\w/g, (part) => part.toUpperCase()).replaceAll("_", " ") : "System user"} · {user?.whatsappNumber || "WhatsApp number not configured"}</div><StatusPill value={destination.available ? "AVAILABLE" : "UNAVAILABLE"} label={destination.available ? "Available" : user && !user.whatsappNumber ? "WhatsApp number not configured" : "Unavailable"} /></div><Button size="icon" variant="ghost" aria-label="Remove user recipient" onClick={() => setDestinationToRemove(destination)}><Trash2 className="h-4 w-4 text-slate-500" /></Button></li>; })}</ul>}</div>
          <div><div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-3 sm:px-5"><MessageCircle className="h-4 w-4 text-slate-500" /><h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">WhatsApp Groups</h3><span className="text-xs text-slate-400">{activeGroupDestinations.length}</span></div>{activeGroupDestinations.length === 0 ? <EmptyState>No WhatsApp groups configured.</EmptyState> : <ul className="divide-y divide-slate-100">{activeGroupDestinations.map((destination) => <li key={destination.destinationId} className="flex items-center justify-between gap-3 px-4 py-3 sm:px-5"><div className="min-w-0"><div className="truncate text-sm font-medium text-slate-800">{destination.groupName || "WhatsApp Group"}</div><StatusPill value={destination.available ? "AVAILABLE" : "UNAVAILABLE"} label={destination.available ? "Available" : "Unavailable"} /></div><Button size="icon" variant="ghost" aria-label="Remove WhatsApp group recipient" onClick={() => setDestinationToRemove(destination)}><Trash2 className="h-4 w-4 text-slate-500" /></Button></li>)}</ul>}</div>
        </div></>}
      </section>}

      {activeTab === "history" && <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 sm:px-5"><div><h2 className="font-display text-base font-semibold text-slate-900">Notification Outbox</h2><p className="mt-0.5 text-xs text-slate-500">Showing {outbox.totalElements} backend records. History filters are not available on this API.</p></div><Button size="sm" variant="outline" onClick={() => loadOutbox(outbox.page, outbox.size)} disabled={outboxLoading}>{outboxLoading ? <Loader2 className="animate-spin" /> : <RefreshCw />}Refresh</Button></div>
        {outboxError && <p className="px-4 pt-3 text-sm text-rose-700 sm:px-5">{outboxError}</p>}
        <div className="overflow-x-auto"><table className="min-w-[1050px] w-full text-sm"><thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-3 py-3 text-left font-medium">Created</th><th className="px-3 py-3 text-left font-medium">Event</th><th className="px-3 py-3 text-left font-medium">Business Ref</th><th className="px-3 py-3 text-left font-medium">Destination</th><th className="px-3 py-3 text-left font-medium">Message</th><th className="px-3 py-3 text-left font-medium">Status</th><th className="px-3 py-3 text-right font-medium">Attempts</th><th className="px-3 py-3 text-left font-medium">Sent</th><th className="px-3 py-3 text-left font-medium">Error</th><th className="px-3 py-3 text-right font-medium">Action</th></tr></thead><tbody className="divide-y divide-slate-100">
          {outboxLoading ? <tr><td colSpan={10}><EmptyState>Loading notification history…</EmptyState></td></tr> : outbox.items.length === 0 ? <tr><td colSpan={10}><EmptyState>No outbox messages found.</EmptyState></td></tr> : outbox.items.map((row) => <tr key={row.outboxId} className="align-top"><td className="whitespace-nowrap px-3 py-3 text-slate-600">{row.createdAt ? dateTime(row.createdAt) : "—"}</td><td className="px-3 py-3"><div className="font-medium text-slate-800">{eventLabel(row.eventType)}</div><div className="text-xs text-slate-500">{row.eventType}</div></td><td className="px-3 py-3 text-slate-700">{row.businessReference ? `${row.businessReference}${row.businessReferenceId ? ` #${row.businessReferenceId}` : ""}` : row.businessReferenceId || "—"}</td><td className="px-3 py-3"><div>{row.destinationType === "WHATSAPP_GROUP" ? "WhatsApp Group" : "User"}</div><div className="max-w-36 truncate text-xs text-slate-500">{row.recipientValue || row.destinationReference || "—"}</div></td><td className="max-w-56 px-3 py-3"><button type="button" onClick={() => setOutboxDetail(row)} className="max-w-56 truncate text-left text-slate-700 hover:underline" title="View message">{row.renderedMessage || "—"}</button></td><td className="px-3 py-3"><StatusPill value={row.status} /></td><td className="px-3 py-3 text-right tabular-nums">{row.attemptCount} / {row.maxAttempts}</td><td className="whitespace-nowrap px-3 py-3 text-slate-600">{row.sentAt ? dateTime(row.sentAt) : "—"}</td><td className="max-w-44 px-3 py-3"><span className="block max-w-44 truncate text-rose-700" title={row.lastError || ""}>{row.lastError || "—"}</span></td><td className="px-3 py-3 text-right"><div className="flex justify-end gap-1">{row.status === "FAILED" && (!isStockPdfEvent(row.eventType) || row.hasAttachment) && <Button size="sm" variant="outline" disabled={retryingId === row.outboxId} onClick={() => retryOutbox(row)}>{retryingId === row.outboxId ? <Loader2 className="animate-spin" /> : <RotateCcw />}{retryingId === row.outboxId ? "Retrying..." : "Retry"}</Button>}{isAdmin && ["SENT", "FAILED"].includes(row.status) && (!isStockPdfEvent(row.eventType) || row.hasAttachment) && <Button size="sm" variant="outline" disabled={resendingId !== null || retryingId === row.outboxId || row.resendPending} onClick={() => { if (!resendKeys.current.has(row.outboxId)) resendKeys.current.set(row.outboxId, newResendKey(row.outboxId)); setResendTarget(row); }}><RotateCcw />Resend</Button>}{isAdmin && ["SENT", "FAILED"].includes(row.status) && isStockPdfEvent(row.eventType) && !row.hasAttachment && <span className="px-2 py-1 text-xs text-slate-500" title="The original PDF was not retained, so it cannot be resent.">PDF unavailable</span>}</div></td></tr>)}
        </tbody></table></div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/50 px-4 py-3 text-sm"><div className="flex items-end gap-3"><div className="w-24"><SelectField label="Rows" value={outbox.size} onChange={(value) => loadOutbox(0, Number(value))} options={[10, 25, 50, 100].map((value) => ({ value, label: String(value) }))} /></div><span className="pb-2 text-slate-600">Page {outbox.totalPages ? outbox.page + 1 : 0} of {outbox.totalPages}</span></div><div className="flex items-center gap-2"><Button size="icon" variant="outline" aria-label="Previous page" disabled={outboxLoading || outbox.page <= 0} onClick={() => loadOutbox(outbox.page - 1, outbox.size)}><ChevronLeft /></Button><Button size="icon" variant="outline" aria-label="Next page" disabled={outboxLoading || outbox.page + 1 >= outbox.totalPages} onClick={() => loadOutbox(outbox.page + 1, outbox.size)}><ChevronRight /></Button></div></div>
      </section>}

      <Dialog open={ruleDialogOpen} onOpenChange={(open) => { if (!ruleSaving) setRuleDialogOpen(open); }}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader><DialogTitle>Configure Notification</DialogTitle><DialogDescription>{eventLabel(editingRule?.eventType)} · {editingRule?.eventType}</DialogDescription></DialogHeader>
          {editingRule && <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3"><p className="text-xs font-semibold uppercase tracking-wide text-slate-500">When this happens</p><p className="mt-1 text-sm leading-5 text-slate-700">{editingRule.description}</p></div>
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 p-3"><div><p className="text-sm font-medium text-slate-800">Recipients</p><p className="mt-0.5 text-xs text-slate-500">{Number(editingRule.recipientCount || 0) === 0 ? "No recipients configured" : `${editingRule.recipientCount} recipient${Number(editingRule.recipientCount) === 1 ? "" : "s"} configured`}</p></div><Button type="button" variant="outline" size="sm" onClick={() => { setSelectedRuleId(String(editingRule.ruleId)); setRuleDialogOpen(false); setActiveTab("destinations"); }}>Manage Recipients</Button></div>
                        {isStockScheduleEvent(editingRule.eventType) ? (
              <div className="grid gap-4 rounded-lg border border-slate-200 p-3 sm:grid-cols-2">
                <div className="flex items-center justify-between gap-3"><div><Label htmlFor="stock-schedule-enabled">Enable scheduled delivery</Label><p className="text-xs text-slate-500">This report has its own schedule and recipients.</p></div><Switch id="stock-schedule-enabled" checked={ruleForm.enabled && ruleForm.whatsappEnabled} onCheckedChange={(enabled) => setRuleForm((current) => ({ ...current, enabled, whatsappEnabled: enabled }))} /></div>
                <div><Label htmlFor="stock-schedule-time">Daily sending time (Sri Lanka time)</Label><input id="stock-schedule-time" type="time" value={ruleForm.scheduleTime} onChange={(event) => setRuleForm((current) => ({ ...current, scheduleTime: event.target.value }))} className="mt-1 block w-full rounded-md border border-slate-200 px-3 py-2 text-sm" required /><p className="mt-1 text-xs text-slate-500">Times use Asia/Colombo (Sri Lanka).</p></div>
              </div>
            ) : (
              <>
                <div className="grid gap-3 rounded-lg border border-slate-200 p-3 sm:grid-cols-2">
                  <div className="flex items-center justify-between gap-3"><div><Label htmlFor="rule-enabled">Rule enabled</Label><p className="text-xs text-slate-500">Controls whether this event rule is active.</p></div><Switch id="rule-enabled" checked={ruleForm.enabled} onCheckedChange={(enabled) => setRuleForm((current) => ({ ...current, enabled }))} /></div>
                  <div className="flex items-center justify-between gap-3"><div><Label htmlFor="whatsapp-enabled">WhatsApp delivery</Label><p className="text-xs text-slate-500">Controls WhatsApp delivery for this rule.</p></div><Switch id="whatsapp-enabled" checked={ruleForm.whatsappEnabled} onCheckedChange={(whatsappEnabled) => setRuleForm((current) => ({ ...current, whatsappEnabled, whatsappActionsEnabled: whatsappEnabled ? current.whatsappActionsEnabled : false }))} /></div>
                  {editingRule.eventType === "CORRECTION_REQUESTED" && <div className="flex items-center justify-between gap-3 sm:col-span-2"><div><Label htmlFor="whatsapp-actions-enabled">Allow Approve/Reject via WhatsApp</Label><p className="text-xs text-slate-500">Allows configured Admin users to approve or reject correction requests directly from WhatsApp.</p></div><Switch id="whatsapp-actions-enabled" checked={ruleForm.whatsappActionsEnabled} disabled={!ruleForm.whatsappEnabled} onCheckedChange={(whatsappActionsEnabled) => setRuleForm((current) => ({ ...current, whatsappActionsEnabled }))} /></div>}
                </div>
                <div><Label htmlFor="rule-template">Message template</Label><Textarea ref={templateRef} id="rule-template" value={ruleForm.messageTemplate} onChange={(event) => setRuleForm((current) => ({ ...current, messageTemplate: event.target.value }))} rows={8} className="mt-1 font-mono text-sm" placeholder="Write the WhatsApp message template" /></div>
                <div><p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Allowed placeholders</p><div className="flex flex-wrap gap-2">{currentRulePlaceholders.length ? currentRulePlaceholders.map((placeholder) => <button key={placeholder} type="button" onClick={() => insertPlaceholder(`{${placeholder}}`)} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 font-mono text-xs text-slate-700 hover:bg-slate-100">{`{${placeholder}}`}</button>) : <span className="text-sm text-slate-500">No placeholders are defined for this event.</span>}</div></div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3"><div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500"><Copy className="h-3.5 w-3.5" />Sample Preview Â· Sample Data</div><pre className="whitespace-pre-wrap break-words font-sans text-sm text-slate-800">{samplePreview(ruleForm.messageTemplate)}</pre></div>
              </>
            )}
            {ruleError && <p className="text-sm text-rose-700">{ruleError}</p>}
          </div>}
          <DialogFooter><Button variant="outline" onClick={() => setRuleDialogOpen(false)} disabled={ruleSaving}>Cancel</Button><Button onClick={saveRule} disabled={!editingRule || ruleSaving} className="bg-navy-800 text-white hover:bg-navy-900">{ruleSaving && <Loader2 className="animate-spin" />}{ruleSaving ? "Saving..." : "Save Rule"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addDestinationOpen} onOpenChange={(open) => !destinationSaving && setAddDestinationOpen(open)}><DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Add Recipient</DialogTitle><DialogDescription>{eventLabel(currentRule?.eventType)} · {currentRule?.description}</DialogDescription></DialogHeader><div className="space-y-4"><SelectField label="Recipient Type" value={destinationType} onChange={(value) => { setDestinationType(value); setSelectedUserId(""); setSelectedGroupId(""); }} options={[{ value: "USER", label: "User" }, { value: "WHATSAPP_GROUP", label: "WhatsApp Group" }]} disabled={destinationSaving} />{destinationType === "USER" ? <><SelectField label="User" value={selectedUserId} onChange={setSelectedUserId} options={selectableUsers.map((user) => ({ value: user.userId, label: `${user.fullName} · ${user.roleName?.toLowerCase().replace(/(^|_)\w/g, (part) => part.toUpperCase()).replaceAll("_", " ")} · ${user.whatsappNumber}` }))} placeholder={usersLoading ? "Loading users…" : "Select a user"} disabled={destinationSaving || usersLoading} />{!selectableUsers.length && !usersLoading && <p className="text-sm text-slate-500">No eligible users available. Liquor Order notifications can be sent to active Admin or Owner users with a WhatsApp number.</p>}</> : <><SelectField label="WhatsApp Group" value={selectedGroupId} onChange={setSelectedGroupId} options={selectableGroups.map((group) => ({ value: group.id, label: group.groupName }))} placeholder="Select a group" disabled={destinationSaving} />{!selectableGroups.length && <p className="text-sm text-slate-500">No available unconfigured groups. Connect WhatsApp and sync groups first.</p>}</>}</div><DialogFooter><Button variant="outline" onClick={() => setAddDestinationOpen(false)} disabled={destinationSaving}>Cancel</Button><Button onClick={() => addDestination(destinationType, destinationType === "USER" ? selectedUserId : selectedGroupId)} disabled={!(destinationType === "USER" ? selectedUserId : selectedGroupId) || destinationSaving || !canAddDestination} className="bg-navy-800 text-white">{destinationSaving && <Loader2 className="animate-spin" />}Add Recipient</Button></DialogFooter></DialogContent></Dialog>

      <Dialog open={Boolean(outboxDetail)} onOpenChange={(open) => !open && setOutboxDetail(null)}><DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto"><DialogHeader><DialogTitle>Notification Message</DialogTitle><DialogDescription>Outbox record #{outboxDetail?.outboxId}</DialogDescription></DialogHeader>{outboxDetail && <dl className="grid gap-3 sm:grid-cols-2">{[
        ["Event", outboxDetail.eventType], ["Business Reference", `${outboxDetail.businessReference || "—"}${outboxDetail.businessReferenceId ? ` · ${outboxDetail.businessReferenceId}` : ""}`], ["Destination Type", outboxDetail.destinationType], ["Destination", outboxDetail.recipientValue || outboxDetail.destinationReference], ["Status", outboxDetail.status], ["Attempts", `${outboxDetail.attemptCount} / ${outboxDetail.maxAttempts}`], ["Attachment", outboxDetail.hasAttachment ? outboxDetail.attachmentFilename : "No attachment retained"], ["Original Entry", outboxDetail.parentOutboxId || "-"], ["Resend Requested By", outboxDetail.resendRequestedBy || "-"], ["Resend Requested At", outboxDetail.resendRequestedAt ? dateTime(outboxDetail.resendRequestedAt) : "-"], ["Created", outboxDetail.createdAt ? dateTime(outboxDetail.createdAt) : "-"], ["Sent At", outboxDetail.sentAt ? dateTime(outboxDetail.sentAt) : "-"], ["Last Error", outboxDetail.lastError || "-"],
      ].map(([label, value]) => <div key={label} className="min-w-0"><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</dt><dd className="mt-1 break-words text-sm text-slate-800">{value}</dd></div>)}<div className="sm:col-span-2"><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Rendered Message</dt><dd className="mt-1 whitespace-pre-wrap break-words rounded-md bg-slate-50 p-3 text-sm text-slate-800">{outboxDetail.renderedMessage}</dd></div></dl>}<DialogFooter><Button variant="outline" onClick={() => setOutboxDetail(null)}>Close</Button></DialogFooter></DialogContent></Dialog>

      <ConfirmDialog open={Boolean(resendTarget)} onOpenChange={(open) => { if (!resendingId && !open) { if (resendTarget) resendKeys.current.delete(resendTarget.outboxId); setResendTarget(null); } }} title="Resend this notification?" description={resendTarget ? `Resend ${resendTarget.attachmentFilename || resendTarget.renderedMessage || eventLabel(resendTarget.eventType)} to ${resendTarget.destinationType === "WHATSAPP_GROUP" ? "WhatsApp group" : "user"} ${resendTarget.recipientValue || resendTarget.destinationReference}?` : ""} confirmText="Resend" loading={resendingId !== null} onConfirm={resendOutbox} />      <ConfirmDialog open={disconnectOpen} onOpenChange={(open) => !disconnecting && setDisconnectOpen(open)} title="Disconnect WhatsApp?" description="WhatsApp notifications will stop until another account is connected. Rules, recipients and message history will be preserved." confirmText="Disconnect" danger loading={disconnecting} onConfirm={disconnect} />
      <ConfirmDialog open={Boolean(destinationToRemove)} onOpenChange={(open) => !removingDestination && !open && setDestinationToRemove(null)} title="Remove destination?" description="This affects future notification events only. Existing outbox messages and history will be preserved." confirmText="Remove" danger loading={removingDestination} onConfirm={removeDestination} />
    </div>
  );
};



